/**
 * F42 (Wave 2 — B2) — venue-scoped Claims tab.
 *
 * Reuses F11's claim review affordances (approve / reject / revoke + the
 * presigned document download) inside the venue admin detail view, scoped to
 * one venue via `GET /admin/venue-claims?venueId=`. Deciding is gated by
 * `canDecide` (super_admin + content_moderator → `venue_claims:review`;
 * support/finance hold only `venue_claims:read`).
 *
 * Data + actions go through an injectable `ClaimsTabApi`; the default wires to
 * the F11 admin claim client. This keeps the component testable with a
 * deterministic fake (no global.fetch plumbing) and avoids modifying the
 * frozen F11 client.
 *
 * Design-flagged: a zoomable claim-doc lightbox is a later polish pass — MVP
 * uses F11's presigned download-and-open fallback.
 */
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  DocumentTile,
  EmptyState,
  FormField,
  Modal,
  Spinner,
  Textarea,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { VenueClaimDocument, VenueClaimEntityStatus } from '@bravemove/types';
import { defaultClaimsTabApi, type ClaimsTabApi, type VenueClaimWithDocs } from './claimsTabApi.js';

export type { ClaimsTabApi, VenueClaimWithDocs } from './claimsTabApi.js';

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_TONE: Record<
  VenueClaimEntityStatus,
  'neutral' | 'info' | 'success' | 'danger' | 'warning'
> = {
  draft: 'neutral',
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  withdrawn: 'neutral',
  revoked_by_admin: 'danger',
};

// ─── Reason modal (reject / revoke) ──────────────────────────────────────────

function ReasonModal({
  open,
  title,
  reasonLabel,
  reasonHelper,
  confirmLabel,
  testIdReason,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  reasonLabel: string;
  reasonHelper: string;
  confirmLabel: string;
  testIdReason: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
    setSubmitting(false);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = reason.trim().length >= 10;
  const submit = () => {
    if (!valid) return;
    setSubmitting(true);
    onConfirm(reason.trim());
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t('admin:venueMerge.claimsTab.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            loading={submitting}
            disabled={!valid || submitting}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <FormField label={reasonLabel} helper={reasonHelper} required>
        <Textarea
          rows={3}
          maxLength={500}
          showCounter
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid={testIdReason}
        />
      </FormField>
    </Modal>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

export interface ClaimsTabProps {
  readonly venueId: string;
  readonly canDecide: boolean;
  readonly api?: ClaimsTabApi;
}

export function ClaimsTab({ venueId, canDecide, api = defaultClaimsTabApi }: ClaimsTabProps) {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState<readonly VenueClaimWithDocs[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.listForVenue(venueId));
    } catch {
      setError(
        t('admin:venueMerge.claimsTab.error', { defaultValue: 'Claims could not be loaded.' }),
      );
    } finally {
      setLoading(false);
    }
  }, [api, venueId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchRows();
  }, [fetchRows]);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const runApprove = async () => {
    if (!approveId) return;
    setBusy(true);
    try {
      await api.approve(approveId);
      setApproveId(null);
      await fetchRows();
    } finally {
      setBusy(false);
    }
  };

  const runReject = async (reason: string) => {
    if (!rejectId) return;
    try {
      await api.reject(rejectId, reason);
      setRejectId(null);
      await fetchRows();
    } finally {
      // ReasonModal owns its own submitting flag.
    }
  };

  const runRevoke = async (reason: string) => {
    if (!revokeId) return;
    try {
      await api.revoke(revokeId, reason);
      setRevokeId(null);
      await fetchRows();
    } finally {
      // ReasonModal owns its own submitting flag.
    }
  };

  const downloadDoc = async (claimId: string, doc: VenueClaimDocument) => {
    try {
      const { url } = await api.getDocumentDownloadUrl(claimId, doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError(
        t('admin:venueMerge.claimsTab.actionError', {
          defaultValue: 'The action could not be completed.',
        }),
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="claims-tab-loading">
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="claims-tab">
      {error ? <Banner tone="danger">{error}</Banner> : null}

      {!canDecide ? (
        <Banner tone="info">
          {t('admin:venueMerge.claimsTab.readOnlyNote', {
            defaultValue: 'You can view claims but not decide them.',
          })}
        </Banner>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="pin" size={28} color="currentColor" />}
          title={t('admin:venueMerge.claimsTab.empty', {
            defaultValue: 'This venue has no claims.',
          })}
        />
      ) : (
        rows.map(({ claim, documents, claimantEmail }) => (
          <Card key={claim.id}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <Badge tone={STATUS_TONE[claim.status]} size="sm">
                  {t(`admin:venueMerge.claimsTab.status.${claim.status}`, {
                    defaultValue: claim.status,
                  })}
                </Badge>
                <span className="font-mono text-[12px] text-ink-3">{claimantEmail ?? '—'}</span>
                <span className="text-[12px] text-ink-3">{formatDate(claim.submittedAt)}</span>
              </div>
              {canDecide ? (
                <div className="flex items-center gap-2">
                  {claim.status === 'pending' ? (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setRejectId(claim.id)}
                        data-testid={`claims-tab-reject-${claim.id}`}
                      >
                        {t('admin:venueMerge.claimsTab.rejectCta', { defaultValue: 'Reject' })}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setApproveId(claim.id)}
                        data-testid={`claims-tab-approve-${claim.id}`}
                      >
                        {t('admin:venueMerge.claimsTab.approveCta', { defaultValue: 'Approve' })}
                      </Button>
                    </>
                  ) : null}
                  {claim.status === 'approved' ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setRevokeId(claim.id)}
                      data-testid={`claims-tab-revoke-${claim.id}`}
                    >
                      {t('admin:venueMerge.claimsTab.revokeCta', { defaultValue: 'Revoke' })}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {documents.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {documents.map((doc) => (
                  <DocumentTile
                    key={doc.id}
                    filename={doc.fileName}
                    sizeBytes={doc.sizeBytes}
                    status="done"
                    onView={() => void downloadDoc(claim.id, doc)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-[12px] text-ink-3">
                {t('admin:venueMerge.claimsTab.noDocuments', {
                  defaultValue: 'No documents attached.',
                })}
              </p>
            )}
          </Card>
        ))
      )}

      {/* Approve confirm */}
      <Dialog
        open={approveId !== null}
        onClose={() => setApproveId(null)}
        onConfirm={() => void runApprove()}
        loading={busy}
        title={t('admin:venueMerge.claimsTab.approveDialogTitle', {
          defaultValue: 'Approve this claim?',
        })}
        description={t('admin:venueMerge.claimsTab.approveDialogBody', {
          defaultValue:
            'Approving grants ownership of the venue to the claimant and promotes it to Tier 2.',
        })}
        confirmLabel={t('admin:venueMerge.claimsTab.approveCta', { defaultValue: 'Approve' })}
        cancelLabel={t('admin:venueMerge.claimsTab.cancel', { defaultValue: 'Cancel' })}
      />

      <ReasonModal
        open={rejectId !== null}
        title={t('admin:venueMerge.claimsTab.rejectDialogTitle', {
          defaultValue: 'Reject this claim',
        })}
        reasonLabel={t('admin:venueMerge.claimsTab.rejectReasonLabel', { defaultValue: 'Reason' })}
        reasonHelper={t('admin:venueMerge.claimsTab.rejectReasonHelper', {
          defaultValue: 'Required — shown to the claimant (min 10 characters).',
        })}
        confirmLabel={t('admin:venueMerge.claimsTab.rejectCta', { defaultValue: 'Reject' })}
        testIdReason="claims-tab-reject-reason"
        onClose={() => setRejectId(null)}
        onConfirm={(reason) => void runReject(reason)}
      />

      <ReasonModal
        open={revokeId !== null}
        title={t('admin:venueMerge.claimsTab.revokeDialogTitle', {
          defaultValue: 'Revoke this approved claim',
        })}
        reasonLabel={t('admin:venueMerge.claimsTab.revokeReasonLabel', { defaultValue: 'Reason' })}
        reasonHelper={t('admin:venueMerge.claimsTab.revokeReasonHelper', {
          defaultValue: 'Required (min 10 characters).',
        })}
        confirmLabel={t('admin:venueMerge.claimsTab.revokeCta', { defaultValue: 'Revoke' })}
        testIdReason="claims-tab-revoke-reason"
        onClose={() => setRevokeId(null)}
        onConfirm={(reason) => void runRevoke(reason)}
      />
    </div>
  );
}
